import type { jsPDF } from 'jspdf';

/** Flatten LLM markdown to clean plain text for a PDF. */
export const plainText = (md?: string | null): string => {
  if (!md) return '';
  return md
    .replace(/```[\s\S]*?```/g, '')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/^\s*[-*]\s+/gm, '• ')
    .replace(/^\s*>\s?/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const TOP_MARGIN = 18;
const BOTTOM_MARGIN = 18;

/**
 * Write a block of plain text with word-wrap and automatic page breaks.
 * Returns the y cursor after the last line.
 */
export const writeParagraphs = (
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  width: number,
  lineHeight = 5
): number => {
  const pageH = doc.internal.pageSize.getHeight();
  for (const para of text.split('\n')) {
    if (para.trim() === '') {
      y += lineHeight * 0.6;
      continue;
    }
    const lines = doc.splitTextToSize(para, width) as string[];
    for (const line of lines) {
      if (y > pageH - BOTTOM_MARGIN) {
        doc.addPage();
        y = TOP_MARGIN;
      }
      doc.text(line, x, y);
      y += lineHeight;
    }
  }
  return y;
};

/**
 * Write a titled "Summary" section built from an LLM markdown narrative.
 * No-op (returns y unchanged) when there's no narrative. Returns the y cursor
 * after the section, with a little trailing space.
 */
export const writeSummarySection = (
  doc: jsPDF,
  title: string,
  markdown: string | null | undefined,
  x: number,
  y: number,
  width: number
): number => {
  const body = plainText(markdown);
  if (!body) return y;

  const pageH = doc.internal.pageSize.getHeight();
  if (y > pageH - 40) {
    doc.addPage();
    y = TOP_MARGIN;
  }
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(124, 58, 237);
  doc.text(title, x, y);
  y += 6;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(30);
  y = writeParagraphs(doc, body, x, y, width, 5);
  return y + 6;
};
